<script>
import safe from 'safe-await'
import { mapGetters } from 'vuex'
import { updateRemoveBgData } from '@/api/settings'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import Notice from '@/components/cars/edit/Notice.vue'
import SectionProgress from '@/components/cars/edit/SectionProgress.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import Form from '@/components/ui/widgets/Form.vue'
import FormSection from '@/components/ui/widgets/FormSection.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'

export default {
  components: {
    'mia-feature-input': FeatureInput,
    'mia-form': Form,
    'mia-form-section': FormSection,
    'mia-form-section-grid': FormSectionGrid,
    'mia-notice': Notice,
    'mia-section-progress': SectionProgress,
    'mia-submit-button': SubmitButton
  },

  mixins: [HandlesBackendValidationErrors],

  data() {
    return {
      accountInfo: {
        attributes: {
          api: {
            free_calls: 0,
            sizes: 'all'
          },
          credits: {
            subscription: 0,
            payg: 0,
            enterprise: 0,
            total: 0
          }
        }
      },
      form: {
        item_removebg_api_key: ''
      }
    }
  },

  computed: {
    ...mapGetters('settings', [
      'settings'
    ])
  },

  watch: {
    settings: {
      immediate: true,
      deep: true,
      handler(settings) {
        this.$set(this.form, 'item_removebg_api_key', settings.item_removebg_api_key || '')

        if (settings.item_removebg_api_key) {
          this.loadAccountInfo()
        }
      }
    }
  },

  created() {
    this.loadAccountInfo()
  },

  methods: {
    async loadAccountInfo() {
      if (this.$wait.is('loading remove.bg account info') || !this.settings.item_removebg_api_key) {
        return
      }

      this.$wait.start('loading remove.bg account info')
      const [error, response] = await safe(window.axios.get(`${process.env.VUE_APP_PROVIDER}/images/removebgaccountinfo`))
      this.$wait.end('loading remove.bg account info')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['remove.bg Account Informationen konnten nicht abgerufen werden']
        })
        return
      }

      this.accountInfo = response.data.data
    },

    async save() {
      this.resetErrors()
      this.$wait.start('updating remove.bg data')

      const [error] = await updateRemoveBgData({
        item_removebg_api_key: this.form.item_removebg_api_key
      })

      this.$wait.end('updating remove.bg data')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Speichern der Daten.']
        })

        this.handleBackendValidationErrors(error)
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Daten erfolgreich gespeichert.']
      })

      await this.$store.dispatch('settings/loadSettings')
    }
  }
}
</script>

<template>
  <mia-form @submit="save">
    <mia-form-section
      title="remove.bg"
      description="Einstellungen für die Verwendung der remove.bg Schnittstelle."
    >
      <mia-wait for="loading remove.bg account info">
        <template #waiting>
          <div class="flex items-center justify-center py-8">
            <mia-wave-loader />
          </div>
        </template>

        <template #default>
          <template v-if="settings.item_removebg_api_key">
            <mia-notice
              type="warning"
              permanent
              class="mb-6"
            >
              <b>Achtung!</b> Sie haben keine remove.bg Credits mehr und können die Schnittstelle nur noch eingeschränkt benutzen. Bitte laden Sie ihr remove.bg Konto <mia-external-link href="https://www.remove.bg/de/pricing">
                mit neuen Credits auf
              </mia-external-link>.
            </mia-notice>
            <mia-notice
              title="Credits"
              class="mb-6"
              permanent
            >
              <div class="grid grid-cols-3 gap-x-6 gap-y-2">
                <div>
                  <b>Abo:</b> {{ accountInfo.attributes.credits.subscription }}
                </div>
                <div>
                  <b>Prepaid:</b> {{ accountInfo.attributes.credits.payg }}
                </div>
                <div>
                  <b>Enterprise:</b> {{ accountInfo.attributes.credits.enterprise }}
                </div>
                <div class="col-span-3">
                  <b>Gesamt:</b> {{ accountInfo.attributes.credits.total }} <span
                    v-if="accountInfo.attributes.credits.total > 0"
                    class="italic"
                  >= {{ accountInfo.attributes.credits.total }} Bilder</span>
                </div>
              </div>
            </mia-notice>
          </template>
          <mia-notice
            v-else
            title="remove.bg"
            class="mb-6"
            permanent
          >
            <p class="py-0 my-0">
              Um die Funktionalität von remove.bg verwenden zu können benötigen Sie ein eigenes <mia-external-link href="https://www.remove.bg/de">
                remove.bg
              </mia-external-link> Konto.<br>
              Einen API-Schlüssel können Sie nach der Registrierung in Ihrem <mia-external-link href="https://www.remove.bg/dashboard#api-key">
                remove.bg Profil
              </mia-external-link> einrichten.
            </p>
          </mia-notice>

          <mia-form-section-grid>
            <!-- DAT Product -->
            <div>
              <mia-feature-input
                v-model="form.item_removebg_api_key"
                label="API-Schlüssel"
                :errors="getErrors('item_removebg_api_key')"
                @input="clearErrors('item_removebg_api_key')"
              />
            </div>
          </mia-form-section-grid>
        </template>
      </mia-wait>
    </mia-form-section>

    <template #actions>
      <div class="flex items-center justify-between w-full">
        <mia-section-progress
          group="settingsBasics"
          page="services"
          section="removeBg"
        />

        <mia-submit-button
          :disabled="$wait.any"
          :is-loading="$wait.is('updating remove.bg data')"
        />
      </div>
    </template>
  </mia-form>
</template>
